'use client';

import { useTranslation } from 'next-i18next';
import { passwordVisibilityIcons, togglePasswordVisibility } from '@auth/forms/formHelpers';
import { loginAction } from '@auth/actions/loginAction';
import { EMAIL_OR_PHONE_PATTERN } from '@auth/constants';
import { BaseAuthForm } from '@auth/forms/BaseAuthForm';
import { LinkStyled } from '@components/common';
import { Input, Button } from '@components/common/Forms';

/** Types */
interface LoginFormProps {
  locale: Locale;
  className?: string;
}

/**
 * @description 'Login' form component.
 * - `locale`: The locale to use for translations.
 * - `className`: Optional class name to pass to the component. It will override the default class name.
 */
export const LoginForm = (props: LoginFormProps) => {
  // Destructure props
  const {
    locale,
    className = '',
  } = props;

  // Helpers
  const { t } = useTranslation('common');

  // Render content
  // *************************
  return (
    <BaseAuthForm
      locale={locale}
      formAction={loginAction}
      className={className}
    >
      {/* Render props from the BaseAuthForm component */}
      {({
        record,
        passwordInputType,
        setPasswordInputType,
        formState,
        isPending,
        formValuesRef,
      }) => (
        <>
          {/* Email or phone */}
          <div className="relative text-left">
            <Input
              required
              type="text"
              className="asOptional"
              placeholder={t('global.label.email_or_phone')}
              pattern={`^${EMAIL_OR_PHONE_PATTERN}$`}
              patternMismatchMessage={t('auth.email.invalid-format.message')}
              {...record('email', {
                defaultValue: formValuesRef?.current?.email || '',
              })}
            />
          </div>

          {/* Password */}
          <div className="relative text-left">
            <Input
              required
              type={passwordInputType}
              className="asOptional"
              placeholder={t('global.label.password')}
              minLength={6}
              {...record('password', {
                defaultValue: formValuesRef?.current?.password || '',
              })}
            />

            {/* Password visibility toggle */}
            <div className="absolute right-1.5 top-2 mt-px">
              <Button
                type="button"
                styling="none"
                className="p-1.5"
                onClick={() => togglePasswordVisibility(setPasswordInputType)}
              >
                {passwordVisibilityIcons[passwordInputType]}
              </Button>
            </div>
          </div>

          {/* Display a general authentication error if any */}
          {formState?.errorText && (
            <div className="mb-2 rounded bg-error/10 p-2 text-sm text-error">
              {formState.errorText}
            </div>
          )}

          {/* Sign in button */}
          <Button
            type="submit"
            color="secondary"
            rounding="full"
            className="mt-2 w-full !font-bold"
            isLoading={isPending}
          >
            {t('sign_in.button')}
          </Button>

          {/* Forgot password */}
          <div className="mt-2">
            <LinkStyled
              styling="text"
              href="/forgot-password"
              size="sm"
              className="font-semibold"
            >
              {t('register.forgotpass')}
            </LinkStyled>
          </div>

          {/* Register */}
          <div className="mt-3 border-t pt-5 text-sm">
            {t('register.registertext')}
            <LinkStyled
              styling="text"
              href="/register"
              size="sm"
              className="ml-1 font-semibold"
            >
              {t('sign_in.register')}
            </LinkStyled>
          </div>
        </>
      )}
    </BaseAuthForm>
  );
};
